import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { chatContextDataSchema } from './chat-context.types';

const c = initContract();

const citationSchema = z.object({
  text: z.string(),
  source: z.object({
    sourceType: z.string().optional(),
    sourceDocId: z.string().optional(),
    sourceTimestamp: z.string().optional(),
    externalUrl: z.string().optional(),
  }),
  startIndex: z.number().optional(),
  endIndex: z.number().optional(),
  relevanceScore: z.number().optional(),
});

const toolInvocationSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  state: z.enum(['input-streaming', 'input-available', 'output-available']),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
});

const messageSchema = z.object({
  id: z.string(),
  content: z.string(),
  citations: z.array(citationSchema).optional(),
  role: z.enum(['user', 'assistant', 'system', 'data']),
  toolInvocations: z.array(toolInvocationSchema).optional(),
  contexts: z.array(chatContextDataSchema).optional(),
});

const threadSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  updatedAt: z.string(),
  createdAt: z.string(),
  messages: z.array(messageSchema).optional(),
});

export type MastraThread = z.infer<typeof threadSchema>;

export const mastraContract = c.router(
  {
    getChatThreads: {
      method: 'GET',
      path: '/:tenantId/threads',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
        page: z.coerce.number().min(1).optional().default(1),
        perPage: z.coerce.number().min(1).optional().default(20),
      }),
      responses: {
        200: z.object({
          threads: z.array(threadSchema),
          total: z.number(),
          hasMore: z.boolean(),
          page: z.number(),
        }),
      },
    },
    getChatThreadMessages: {
      method: 'GET',
      path: '/:tenantId/threads/:threadId/messages',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
        includeSources: z
          .string()
          .optional()
          .transform((val) => val === 'true'),
      }),
      responses: {
        200: z.object({
          messages: z.array(messageSchema),
        }),
      },
    },
    getChatThreadTitle: {
      method: 'GET',
      path: '/:tenantId/threads/:threadId/title',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
      }),
      responses: {
        200: z.object({
          title: threadSchema.shape.title,
        }),
      },
    },
    createChatThread: {
      method: 'POST',
      path: '/:tenantId/threads',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      body: z.object({
        patientId: z.string().optional(),
      }),
      responses: {
        201: z.object({
          threadId: z.string(),
        }),
      },
    },
    updateThreadTitle: {
      method: 'PATCH',
      path: '/:tenantId/threads/:threadId/title',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
      }),
      body: z.object({
        title: z.string().optional(),
        autoGenerate: z.boolean().optional(),
      }),
      responses: {
        200: z.object({
          title: z.string(),
        }),
        400: z.object({
          error: z.string(),
        }),
      },
    },
    // Rehydrates the Plan Builder's useChat from a `?thread=` deep link — the
    // living draft is conversation state (the latest presentPlan emission), so
    // restoring the thread restores the draft. Messages are AI SDK v5
    // UIMessages (typed loosely; the client casts).
    getPlanBuilderThread: {
      method: 'GET',
      path: '/:tenantId/plan-builder/threads/:threadId',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      responses: {
        200: z.object({
          title: z.string().nullable(),
          messages: z.array(z.unknown()),
        }),
        404: z.object({
          error: z.string(),
        }),
      },
    },
    // Rehydrates the Care Plan Tailor's useChat from a `?thread=` deep link —
    // the living draft is conversation state (the latest presentPlanInstance
    // emission), so restoring the thread restores the tailored draft (ENG-4699).
    getCarePlanTailorThread: {
      method: 'GET',
      path: '/:tenantId/care-plan-tailor/threads/:threadId',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      responses: {
        200: z.object({
          title: z.string().nullable(),
          messages: z.array(z.unknown()),
        }),
        404: z.object({
          error: z.string(),
        }),
      },
    },
    // Switch the appointment's draft Care Plan to a different matched plan
    // (ENG-4764). Re-instantiates the draft from the chosen PlanDefinition (fresh
    // phases/activities) and resets the Tailor thread — prior tailoring is
    // discarded. `canonical` must be one of the alternatives persisted on the
    // draft's match extension.
    switchCarePlanDraft: {
      method: 'POST',
      path: '/:tenantId/care-plan-draft/:appointmentId/switch',
      pathParams: z.object({
        tenantId: z.string(),
        appointmentId: z.string(),
      }),
      body: z.object({
        canonical: z.string(),
      }),
      responses: {
        200: z.object({
          carePlanId: z.string(),
        }),
        400: z.object({ error: z.string() }),
        403: z.object({ error: z.string() }),
        404: z.object({ error: z.string() }),
      },
    },
    getSpecTemplates: {
      method: 'GET',
      path: '/:tenantId/spec-templates',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      responses: {
        200: z.object({
          templates: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
              description: z.string().nullable(),
            }),
          ),
        }),
      },
    },
  },
  { pathPrefix: '/mastra' },
);
